import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { Zap, Bell, Trophy, Factory, Swords, Gift, Target, BookOpen } from 'lucide-react';
import { FaImages } from 'react-icons/fa';
import BookmarkIcon from '../Icons/BookmarkIcon';
import { questsAPI } from '../../api/gameAPI';
import { useSelector, useDispatch } from 'react-redux';
import {
    selectGameDataSummary,
    selectEnergyStatus,
    selectGameDataLoading,
    fetchGameDataSummary,
    fetchEnergyStatus
} from '../../redux/slices/gameDataSlice';
import {
    selectUnreadCount,
    fetchUnreadCount
} from '../../redux/slices/notificationsSlice';
import { useAuth } from '../../contexts/AuthContext';
import { useUserLevel } from '../../hooks/useUserLevel';
import { useAuthReadyWithUser } from '../../hooks/useAuthReady';
import { getUnviewedCount } from '../../api/wall_posts';
import { getCatalogLevel } from '../../api/catalogLevels';
import { useQuery } from 'react-query';
import LevelUpPopup from '../Catalog/LevelUpPopup';
import LevelInfoPopup from '../Catalog/LevelInfoPopup';
import StatsPopup from '../Catalog/StatsPopup';

const APP_MODE = import.meta.env.VITE_APP_MODE || 'full';
const isGame = APP_MODE === 'game' || APP_MODE === 'full';

const GameResourcesBar = React.memo(() => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [isLevelHovered, setIsLevelHovered] = React.useState(false);
    const [isOverflowVisible, setIsOverflowVisible] = React.useState(true);
    const timeoutRef = React.useRef(null);

    const dispatch = useDispatch();
    const { user } = useAuth();
    const { hasFeature, userLevel, refreshUserLevel } = useUserLevel();
    const isAuthReady = useAuthReadyWithUser();
    
    // Дополнительная проверка пользователя из Redux store
    const userFromRedux = useSelector(state => state.auth.user);
    const isAuthenticated = useSelector(state => state.auth.isAuthenticated);
    
    console.log('🎮 GameResourcesBar: auth state', { 
        user, 
        userFromRedux, 
        isAuthenticated,
        userMatch: user === userFromRedux 
    });
    
    // Получаем игровые данные из Redux store
    const gameDataSummary = useSelector(selectGameDataSummary);
    const energyStatus = useSelector(selectEnergyStatus);
    const { summary: summaryLoading } = useSelector(selectGameDataLoading);
    
    // Получаем количество непрочитанных уведомлений
    // TODO: В каталоге фильтровать только каталожные уведомления (postcard_claimed, greeting_received)
    // Игровые уведомления (production_ready, level_up) не должны показываться в каталоге
    const unreadCount = useSelector(selectUnreadCount);

    // Бейдж квестов (есть ли незабранные награды)
    const [questBadge, setQuestBadge] = React.useState(false);

    // Каталог: навигация + уровень
    const isCatalog = !isGame;
    const [levelUpShow, setLevelUpShow] = React.useState(null);
    const [showLevelInfo, setShowLevelInfo] = React.useState(false);
    const [showStats, setShowStats] = React.useState(false);
    const prevLevelRef = React.useRef(null);

    const { data: catalogLevelData } = useQuery(
        ['catalog-level'], () => getCatalogLevel(),
        { enabled: isCatalog && isAuthReady, staleTime: 10 * 1000 }
    );
    const { data: unviewedData } = useQuery(
        ['wall-unviewed-count'], () => getUnviewedCount(),
        { enabled: isCatalog && isAuthReady, staleTime: 30 * 1000, refetchInterval: 60 * 1000 }
    );
    const wallUnviewed = unviewedData?.count || 0;

    React.useEffect(() => {
        if (!catalogLevelData) return;
        if (prevLevelRef.current !== null && catalogLevelData.level > prevLevelRef.current) {
            setLevelUpShow(catalogLevelData.level);
        }
        prevLevelRef.current = catalogLevelData.level;
    }, [catalogLevelData?.level]);

    // Загружаем данные только после авторизации
    React.useEffect(() => {
        console.log('🎮 GameResourcesBar: useEffect triggered', { user, pathname: location.pathname });

        // Не загружаем данные на страницах авторизации
        const currentPath = location.pathname;
        if (currentPath === '/auth' || currentPath === '/telegram/auth') {
            console.log('🎮 GameResourcesBar: на странице авторизации, пропускаем загрузку данных');
            return;
        }

        // Данные теперь загружаются в AuthContext, здесь только читаем из Redux
        console.log('🎮 GameResourcesBar: данные загружаются в AuthContext, читаем из Redux');
        // Обновляем уровень при навигации между страницами
        if (refreshUserLevel) refreshUserLevel();

        // Проверяем бейдж квестов (только в игровом режиме)
        if (isGame) {
            questsAPI.getActive().then(data => {
                setQuestBadge(data.has_unclaimed_story || false);
            }).catch(() => {});
        }
    }, [isAuthReady, location.pathname, refreshUserLevel]);
    
    // Проверяем, нужно ли показывать луну вместо солнца
    const showMoon = false; // Отключено: всегда показываем солнце

    // Используем данные из API или дефолтные значения
    const resources = React.useMemo(() => ({
        energy: energyStatus?.yang_energy || gameDataSummary?.energy || 20,
        level: userLevel?.level || gameDataSummary?.level || 0,
        notifications: unreadCount || 0
    }), [gameDataSummary, energyStatus, unreadCount, userLevel]);

    // Определяем активную вкладку игровых данных
    const isGameDataActive = location.pathname === '/game-data';
    const gameDataTab = new URLSearchParams(location.search).get('tab') || 'resources';

    const ResourceItem = ({ icon: Icon, value, color, onClick, isActive = false }) => (
        <button
            onClick={onClick}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg backdrop-blur-sm transition-colors ${
                isActive 
                    ? 'bg-[#FFC107] text-white shadow-lg' 
                    : 'bg-white/20 dark:bg-gray-800/50 hover:bg-white/30 dark:hover:bg-gray-700/50'
            }`}
        >
            <Icon className={`w-5 h-5 ${isActive ? 'text-white' : color}`} />
            <span className={`text-sm font-semibold ${isActive ? 'text-white' : 'text-white dark:text-white'}`}>
                {value}
            </span>
        </button>
    );

    const IconItem = ({ icon: Icon, label, color, badge, onClick, isActive = false }) => (
        <button
            onClick={onClick}
            className={`relative flex items-center justify-center w-12 h-12 rounded-lg backdrop-blur-sm transition-colors ${
                isActive 
                    ? 'bg-[#FFC107] shadow-lg' 
                    : 'bg-white/20 dark:bg-gray-800/50 hover:bg-white/30 dark:hover:bg-gray-700/50'
            }`}
        >
            <Icon className={`w-6 h-6 ${isActive ? 'text-white' : color}`} />
            {badge && badge > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {badge}
                </span>
            )}
            <span className="sr-only">{label}</span>
        </button>
    );

    // Дебаунс для overflow состояния
    const handleHoverChange = (isHovered) => {
        setIsLevelHovered(isHovered);
        
        if (isHovered) {
            // Сразу показываем при наведении
            setIsOverflowVisible(true);
            // Очищаем предыдущий таймер
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
                 } else {
             // Скрываем через 10мс после ухода курсора
             if (timeoutRef.current) {
                 clearTimeout(timeoutRef.current);
             }
             timeoutRef.current = setTimeout(() => {
                 setIsOverflowVisible(false);
             }, 10);
         }
    };

    // При загрузке компонента показываем overflow на 2 секунды, затем скрываем
    React.useEffect(() => {
        const initialTimeout = setTimeout(() => {
            setIsOverflowVisible(false);
        }, 2000);

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            clearTimeout(initialTimeout);
        };
    }, []);

    const LevelItem = React.memo(({ level, onHoverChange, showMoon }) => {
        const [isHovered, setIsHovered] = React.useState(false);
        const [isAnimating, setIsAnimating] = React.useState(false);
        const animationTimeoutRef = React.useRef(null);
        
        // Создаем массив углов для лучей (от 45° до -105°, 16 лучей) - выносим за пределы компонента
        const rayAngles = React.useMemo(() => [95, 85, 75, 65, 55, 45, 35, 25, 15, 5, -5, -15, -25, -35, -45], []);
        
        // CSS для анимации освещения под солнцем - выносим за пределы компонента
        const sunPulseAnimation = React.useMemo(() => `
            @keyframes sunPulse {
                0% { 
                    box-shadow: 0 0 20px rgba(250, 204, 21, 0.4);
                }
                50% { 
                    box-shadow: 0 0 30px rgba(255, 255, 255, 0.6);
                }
                100% { 
                    box-shadow: 0 0 20px rgba(250, 204, 21, 0.4);
                }
            }
        `, []);
        
        // CSS для анимации свечения луны
        const moonPulseAnimation = React.useMemo(() => `
            @keyframes moonPulse {
                0% { 
                    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
                }
                50% { 
                    box-shadow: 0 0 30px rgba(255, 255, 255, 0.5);
                }
                100% { 
                    box-shadow: 0 0 20px rgba(255, 255, 255, 0.3);
                }
            }
        `, []);
        
                 // Очистка таймера при размонтировании
         React.useEffect(() => {
             return () => {
                 if (animationTimeoutRef.current) {
                     clearTimeout(animationTimeoutRef.current);
                 }
             };
         }, []);
         
         return (
             <div className="relative">
                 <style>{showMoon ? moonPulseAnimation : sunPulseAnimation}</style>
                 
                 {showMoon ? (
                     // Рендеринг луны - точная копия из BottomNavigation
                     <button
                        onClick={() => {
                            if (!isGame) return;
                            if (hasFeature('GAME_ENERGY')) {
                                navigate('/game');
                            }
                        }}
                        onMouseEnter={() => {
                            setIsHovered(true);
                            onHoverChange(true);
                        }}
                        onMouseLeave={() => {
                            setIsHovered(false);
                            onHoverChange(false);
                        }}
                        className={`relative w-16 h-16 transform scale-[1.4] absolute transition-all duration-300 group ${
                            isGame && hasFeature('GAME_LEVEL')
                                ? 'cursor-pointer'
                                : 'cursor-default opacity-50'
                        }`}
                        style={{ right: '6px', top: '8px', transform: 'scale(1.4)' }}
                        disabled={!hasFeature('GAME_LEVEL')}
                    >
                        <svg 
                            viewBox="0 0 120 120" 
                            className="w-16 h-16 transform scale-x-[-1] transition-all duration-300"
                            style={{
                                filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))'
                            }}
                        >
                            <path 
                                fill="url(#moonGradient)" 
                                d="M96.484,92.7c-27.499,0-49.792-22.294-49.792-49.794c0-15.768,7.338-29.815,18.777-38.938
                                C35.655,5.082,11.823,29.588,11.823,59.674c0,30.794,24.965,55.757,55.759,55.757c19.959,0,37.459-10.493,47.311-26.262
                                C109.195,91.441,102.99,92.7,96.484,92.7z"
                            />
                            <defs>
                                <linearGradient id="moonGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="rgba(255, 255, 255, 0.95)" />
                                    <stop offset="100%" stopColor="rgba(209, 213, 219, 0.9)" />
                                </linearGradient>
                            </defs>
                        </svg>
                        
                        
                        {/* Свечение от полумесяца */}
                        <div className="absolute inset-0 w-16 h-16 bg-white/20 rounded-full blur-sm animate-pulse pointer-events-none group-hover:bg-white/40 group-hover:blur-md transition-all duration-300"></div>
                        {/* Дополнительное свечение при наведении */}
                        <div className="absolute inset-0 w-16 h-16 bg-white/10 rounded-full blur-lg opacity-0 group-hover:opacity-100 group-hover:bg-white/30 transition-all duration-300 pointer-events-none"></div>
                    </button>
                 ) : (
                     // Рендеринг солнца (оригинальный код)
                     <div
                        className={`absolute -left-12 transform -translate-y-1/2 w-24 h-24 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 cursor-pointer hover:scale-110 ${
                            isAnimating ? 'animate-[sunPulse_2s_ease-in-out_infinite]' : ''
                        }`}
                        style={{
                            background: 'linear-gradient(to bottom right, rgba(250, 204, 21, 1), rgba(251, 146, 60, 1))'
                        }}
                        onClick={() => {
                            if (isCatalog) {
                                setShowStats(true);
                            } else if (hasFeature('GAME_ENERGY')) {
                                navigate('/game');
                            }
                        }}
                        onMouseEnter={() => {
                            setIsHovered(true);
                            onHoverChange(true);
                            setIsAnimating(true);
                        }}
                        onMouseLeave={() => {
                            setIsHovered(false);
                            onHoverChange(false);
                            
                            if (animationTimeoutRef.current) {
                                clearTimeout(animationTimeoutRef.current);
                            }
                            animationTimeoutRef.current = setTimeout(() => {
                                setIsAnimating(false);
                            }, 10);
                        }}
                    >
                        {/* Свечение от солнца при наведении */}
                        <div className={`absolute inset-0 w-24 h-24 bg-yellow-400/30 rounded-full blur-md transition-all duration-300 pointer-events-none ${
                            isHovered ? 'opacity-100 scale-110' : 'opacity-0 scale-100'
                        }`}></div>
                        {/* Дополнительное свечение при наведении */}
                        <div className={`absolute inset-0 w-24 h-24 bg-yellow-300/20 rounded-full blur-lg transition-all duration-300 pointer-events-none ${
                            isHovered ? 'opacity-100 scale-125' : 'opacity-0 scale-100'
                        }`}></div>
                    </div>
                 )}
                 
                 {/* Лучи солнца - показываем только если не луна */}
                 {!showMoon && (
                     <div className="absolute left-24 transform -translate-y-1/2 w-24 h-24" style={{ zIndex: -1 }}>
                         {rayAngles.map((angle, index) => (
                             <div
                                 key={index}
                                 style={{
                                     position: 'absolute',
                                     top: '50%',
                                     left: '50%',
                                     width: '300px',
                                     height: '2px',
                                     background: 'linear-gradient(to right, rgba(250, 204, 21, 0.6), transparent)',
                                     transform: `translate(-50%, -50%) rotate(${angle}deg)`,
                                     transformOrigin: '0 50%',
                                     zIndex: -1,
                                     pointerEvents: 'none'
                                 }}
                             />
                         ))}
                     </div>
                 )}
            </div>
        );
    });

    // Если функция BASE_GAME отключена, не рендерим панель вообще
    if (!hasFeature('BASE_GAME')) {
        return null;
    }


    return (
        <div className="fixed top-0 left-0 right-0 bg-gradient-to-r from-sky-400/90 to-purple-600/90 dark:from-sky-500/90 dark:to-purple-700/90 backdrop-blur-sm z-40" style={{ overflow: isOverflowVisible ? 'visible' : 'hidden' }}>
            <div className="flex items-center justify-between px-4 h-[75px]">
                {/* Левая часть - ресурсы */}
                <div className="flex items-center gap-2">
                    <LevelItem level={resources.level} onHoverChange={handleHoverChange} showMoon={showMoon} />
                    <div className={showMoon ? "mr-12" : "ml-12"}>
                        {/* Каталог: уровень рядом с солнцем */}
                        {isCatalog && catalogLevelData && (
                            <button
                                onClick={() => setShowLevelInfo(true)}
                                className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white/20 hover:bg-white/30 transition-colors cursor-pointer"
                                title={`${catalogLevelData.current_xp}/${catalogLevelData.xp_for_next} XP`}
                            >
                                <span className="text-xs font-bold text-white">{catalogLevelData.level}</span>
                                <div className="w-8 h-1.5 bg-white/30 rounded-full overflow-hidden">
                                    <div className="h-full bg-amber-300 rounded-full transition-all" style={{ width: `${Math.round((catalogLevelData.current_xp / catalogLevelData.xp_for_next) * 100)}%` }} />
                                </div>
                            </button>
                        )}
                        {/* Показываем кнопку энергии только в игровом режиме */}
                        {isGame && hasFeature('GAME_ENERGY') && (
                            <button
                                onClick={() => navigate('/game?tab=level')}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg backdrop-blur-sm transition-colors ${
                                    isGameDataActive && gameDataTab === 'level'
                                        ? 'bg-[#FFC107] text-white shadow-lg' 
                                        : 'bg-white/20 dark:bg-gray-800/50 hover:bg-white/30 dark:hover:bg-gray-700/50'
                                }`}
                            >
                                <span className={`text-sm font-semibold ${
                                    isGameDataActive && gameDataTab === 'level' 
                                        ? 'text-white' 
                                        : 'text-white dark:text-white'
                                }`}>
                                    {resources.level} {t('resources.levelShort')}
                                </span>
                            </button>
                        )}
                    </div>
                </div>

                {/* Правая часть */}
                <div className="flex items-center gap-2">
                    {isCatalog ? (<>
                        {[
                            { path: '/gallery', icon: <FaImages className="w-5 h-5" />, label: 'Галерея' },
                            { path: '/stone', icon: <BookOpen className="w-5 h-5" />, label: 'Гостевая' },
                            ...(hasFeature('BOOKMARKS_USE') ? [{ path: '/bookmarks', icon: <BookmarkIcon size={20} />, label: 'Избранное' }] : []),
                        ].map(item => {
                            const active = item.path === '/gallery' ? (location.pathname === '/' || location.pathname.startsWith('/gallery')) : location.pathname.startsWith(item.path);
                            return (
                                <button key={item.path} onClick={() => navigate(item.path)}
                                    className={`relative flex items-center justify-center w-10 h-10 rounded-lg transition-all ${active ? 'bg-[#FFC107] text-white shadow-lg' : 'bg-white/15 text-white/80 hover:bg-white/25'}`}
                                    title={item.label}>
                                    {item.icon}
                                    {item.badge > 0 && (
                                        <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] bg-red-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold px-1">
                                            {item.badge > 99 ? '99+' : item.badge}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {levelUpShow && <LevelUpPopup level={levelUpShow} onClose={() => setLevelUpShow(null)} />}
                        {showLevelInfo && catalogLevelData && (
                            <LevelInfoPopup
                                level={catalogLevelData.level}
                                currentXp={catalogLevelData.current_xp}
                                xpForNext={catalogLevelData.xp_for_next}
                                onClose={() => setShowLevelInfo(false)}
                            />
                        )}
                        {showStats && <StatsPopup onClose={() => setShowStats(false)} />}
                    </>) : (<>
                        {/* Игра: оригинальные иконки */}
                        {hasFeature('GAME_CRAFT') && (
                            <IconItem icon={() => (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="19" x2="12" y2="15.46"/><line x1="12" y1="8.54" x2="12" y2="5"/>
                                    <polyline points="16 1 12 5 8 1"/><polyline points="8 23 12 19 16 23"/>
                                    <line x1="18.06" y1="15.5" x2="15" y2="13.73"/><line x1="9" y1="10.27" x2="5.94" y2="8.5"/>
                                    <polyline points="4.47 3.04 5.94 8.5 0.47 9.96"/><polyline points="19.53 20.96 18.06 15.5 23.53 14.04"/>
                                    <line x1="9" y1="13.73" x2="5.94" y2="15.5"/><line x1="18.06" y1="8.5" x2="15" y2="10.27"/>
                                    <polyline points="19.53 3.04 18.06 8.5 23.53 9.96"/><polyline points="4.47 20.96 5.94 15.5 0.47 14.04"/>
                                    <polygon points="15 10.27 15 13.73 12 15.46 9 13.73 9 10.27 12 8.54 15 10.27"/>
                                    <line x1="4" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="20" y2="12"/>
                                    <line x1="8" y1="18.93" x2="9" y2="17.2"/><line x1="15" y1="6.8" x2="16" y2="5.07"/>
                                    <line x1="16" y1="18.93" x2="15" y2="17.2"/><line x1="9" y1="6.8" x2="8" y2="5.07"/>
                                </svg>
                            )} label={t('game.crafting', 'Крафт')} color="text-white" onClick={() => navigate('/game')} isActive={location.pathname === '/crafting'} />
                        )}
                        {hasFeature('GAME_BATTLE') && (
                            <IconItem icon={Swords} label={t('game.battle', 'Битва')} color="text-white" onClick={() => navigate('/game')} isActive={location.pathname === '/battle'} />
                        )}
                        {hasFeature('GAME_CHRISTMAS') && (
                            <IconItem icon={Gift} label={t('game.christmas', 'Рождество')} color="text-white" onClick={() => navigate('/game')} isActive={location.pathname === '/christmas'} />
                        )}
                        {hasFeature('GAME_NOTIFICATIONS') && (
                            <IconItem icon={Bell} label="Уведомления" color="text-white"
                                badge={resources.notifications > 0 ? resources.notifications : undefined}
                                onClick={() => navigate('/notifications')} isActive={location.pathname === '/notifications'} />
                        )}
                        {isGame && hasFeature('GAME_NOTIFICATIONS') && (
                            <IconItem icon={Target} label="Задания" color="text-white"
                                badge={questBadge ? 1 : undefined}
                                onClick={() => navigate('/game?tab=quests')} isActive={isGameDataActive && gameDataTab === 'quests'} />
                        )}
                    </>)}
                </div>
            </div>
        </div>
    );
});

export default GameResourcesBar; 